import React, { useState } from "react";
import { companyLabel, creditsAsUsd, formatDecimal } from "./helpers";

const EMPTY = { company_id: "", credits: "", note: "" };

/**
 * Adds credits to one company's balance by hand.
 *
 * @param {object} props - Component props.
 * @param {object[]} props.companies - Companies credits may be added to.
 * @param {string} props.creditsPerUsd - Credits one dollar buys, for the money hint.
 * @param {Function} props.onSelectCompany - Called with the company id to read its balance.
 * @param {Function} props.onGrant - Called with the company id and the form body.
 * @param {object|null} props.balance - The selected company's balance, once read.
 * @param {boolean} props.loadingBalance - Whether a balance read is in flight.
 * @param {boolean} props.saving - Whether a grant is in flight.
 * @returns {JSX.Element} The grant card.
 *
 * @remarks
 * The balance is shown before and after, because a grant that cannot be seen
 * landing is indistinguishable from one that silently failed. Choosing a
 * company reads it; granting replaces it with what the ledger returned, which
 * is the figure the grant actually produced rather than the one it intended.
 *
 * Credits are entered, not dollars, with the money shown alongside. The ledger
 * stores credits, and a figure captured in dollars would have to be converted
 * at the ratio in force the moment it was typed — leaving a row whose meaning
 * changes the next time that ratio moves.
 */
const GrantCreditsCard = ({
  companies = [],
  creditsPerUsd,
  onSelectCompany,
  onGrant,
  balance,
  loadingBalance,
  saving,
}) => {
  const [draft, setDraft] = useState(EMPTY);

  const selectCompany = (event) => {
    const companyId = event.target.value;
    setDraft({ ...draft, company_id: companyId });

    if (companyId !== "") {
      onSelectCompany(Number(companyId));
    }
  };

  const submit = async (event) => {
    event.preventDefault();

    const granted = await onGrant(Number(draft.company_id), {
      credits: draft.credits,
      note: draft.note,
    });

    if (granted) {
      setDraft({ ...draft, credits: "", note: "" });
    }
  };

  const money = creditsAsUsd(draft.credits, creditsPerUsd);
  const complete = draft.company_id !== "" && draft.credits !== "";

  return (
    <form className="aicr-card" onSubmit={submit}>
      <h2>Add credits</h2>
      <p className="aicr-card-sub">
        Credits added here are spendable immediately and no money changes hands.
        The reason is recorded against your account and appears in the company's
        own transaction history.
      </p>

      <div className="aicr-grid">
        <div className="aicr-field">
          <label htmlFor="aicr-grant-company">Company</label>
          <select
            id="aicr-grant-company"
            value={draft.company_id}
            onChange={selectCompany}
          >
            <option value="">Select a company…</option>
            {companies.map((company) => (
              <option key={company.id} value={String(company.id)}>
                {companyLabel(company)}
              </option>
            ))}
          </select>
          <span className="aicr-hint">
            {draft.company_id === ""
              ? "Who receives the credits."
              : loadingBalance
                ? "Reading balance…"
                : balance
                  ? `Balance now: ${formatDecimal(balance.credits, 2)} credits (${creditsAsUsd(
                      balance.credits,
                      balance.creditsPerUsd,
                    )}).`
                  : "Balance unavailable."}
          </span>
        </div>

        <div className="aicr-field">
          <label htmlFor="aicr-grant-credits">Credits</label>
          <input
            id="aicr-grant-credits"
            type="text"
            inputMode="decimal"
            value={draft.credits}
            onChange={(event) =>
              setDraft({ ...draft, credits: event.target.value })
            }
          />
          <span className="aicr-hint">
            {money ? `Worth ${money} at today's rate.` : "Entered in credits."}
          </span>
        </div>

        <div className="aicr-field">
          <label htmlFor="aicr-grant-note">Reason</label>
          <input
            id="aicr-grant-note"
            type="text"
            maxLength={255}
            value={draft.note}
            onChange={(event) =>
              setDraft({ ...draft, note: event.target.value })
            }
          />
          <span className="aicr-hint">Shown to the company. Optional.</span>
        </div>
      </div>

      <div className="aicr-actions">
        <button
          type="submit"
          className="aicr-btn aicr-btn-primary"
          disabled={!complete || saving}
        >
          {saving ? "Adding…" : "Add credits"}
        </button>
      </div>
    </form>
  );
};

export default GrantCreditsCard;
